/**
 * ActionsSection - Actions list that opens proposals in the slideout.
 */

import type React from 'react';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { getOnboardingSortPriority } from '@/components/proposals/shared/utils';
import { SeatUpgradeCard } from '@/components/seat-gate/SeatUpgradeCard';
import { useFeatureAccess } from '@/hooks/usePermissions';
import { markOnce } from '@/lib/boot/bootMetrics';
import type { ProposalListItem } from '@/lib/proposals/core/types';

import { ActionRow } from './ActionRow';
import { MAX_VISIBLE_ACTIONS } from './constants';
import { ActionRowSkeleton } from './DashboardHomeSkeleton';

/** Stable keys for the loading rows (never index-keyed). */
const LOADING_ROW_KEYS = Array.from(
  { length: MAX_VISIBLE_ACTIONS },
  (_, i) => `loading-${i}`
);

interface ActionsSectionProps {
  /** Action items to display */
  actions: ProposalListItem[];
  /**
   * Whether the proposals query is still in flight. While true and no rows
   * are available yet, skeleton rows render instead of the empty state —
   * "no data yet" must never read as "nothing to do".
   */
  isLoading?: boolean;
  /** Organization ID for API calls */
  orgId: number;
  /** Whether this is onboarding-only mode */
  onboardingOnly?: boolean;
  /** Onboarding actions for onboarding-only mode (full ProposalListItem from API) */
  onboardingActions?: ProposalListItem[];
  /** Whether user has started onboarding */
  hasStartedOnboarding?: boolean;
  /**
   * Whether the first actions are gated on the user creating their deal pipeline.
   * When true, the onboarding "analyzing" state is replaced with a message
   * pointing the user at the create-pipeline step (mirrors the backend gate:
   * create_proposals parks until a deal pipeline exists).
   */
  isWaitingForPipeline?: boolean;
  /** Callback when user starts an action */
  onStartAction?: (proposalId: number) => void;
  /** Callback when user dismisses an action */
  onDismissAction: (proposalId: number) => Promise<void>;
  /** Set of selected action IDs */
  selectedIds: Set<number>;
  /** Callback to toggle selection of an action (supports shift-click via shiftKey param) */
  onToggleSelection: (id: number, shiftKey?: boolean) => void;
  /** Optional ref to the first action's checkbox, for focus restoration after bulk dismiss */
  firstActionCheckboxRef?: React.RefObject<HTMLButtonElement | null>;
}

/**
 * ActionsSection - Main actions section component
 */
export function ActionsSection({
  actions,
  isLoading = false,
  orgId,
  onboardingOnly = false,
  onboardingActions: onboardingActionsProp = [],
  hasStartedOnboarding = false,
  isWaitingForPipeline = false,
  onStartAction,
  onDismissAction,
  selectedIds,
  onToggleSelection,
  firstActionCheckboxRef,
}: ActionsSectionProps) {
  const { hasFeature } = useFeatureAccess();
  const hasAiActions = hasFeature('ai_actions');

  // Limit displayed actions
  const displayedActions = useMemo(
    () => actions.slice(0, MAX_VISIBLE_ACTIONS),
    [actions]
  );

  // Perf trace: when this section first rendered, and when it first rendered
  // with rows (the two differ while the proposals query is still in flight).
  useEffect(() => {
    markOnce('section:actions:rendered');
    if (actions.length > 0) {
      markOnce('section:actions:content', { count: actions.length });
    }
  }, [actions.length]);

  // Viewer-seat gate: replace the section body with the compact upsell card.
  // The proposals query is also gated server-side, so we skip the body entirely.
  if (!hasAiActions) {
    return (
      <div className='space-y-3'>
        <div className='flex items-center justify-between mt-0.5 mb-3'>
          <h2 className='text-[15px] font-semibold text-[#181d27] dark:text-neutral-100'>
            AI Actions
          </h2>
        </div>
        <SeatUpgradeCard feature='ai_actions' variant='compact' />
      </div>
    );
  }

  // Header with "See all actions" link (per Figma design)
  const renderHeader = () => (
    <div className='flex items-center justify-between mt-0.5 mb-3'>
      <h2 className='text-[15px] font-semibold text-[#181d27] dark:text-neutral-100'>
        AI Actions
      </h2>
      <Link
        to={`/${orgId}/actions`}
        className='text-[12px] font-semibold text-black dark:text-neutral-100 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors'
      >
        See all actions
      </Link>
    </div>
  );

  // Onboarding-only mode: show onboarding-specific state
  if (onboardingOnly) {
    // Use the separately fetched onboarding actions (passed as prop from parent)
    // Sort using same priority order as proposals center (getOnboardingSortPriority)
    const sortedOnboardingActions = [...onboardingActionsProp].sort(
      (a, b) => getOnboardingSortPriority(a) - getOnboardingSortPriority(b)
    );

    // If we have onboarding actions, show all of them
    if (sortedOnboardingActions.length > 0) {
      return (
        <div className='space-y-3'>
          {renderHeader()}
          <div className='space-y-1.5'>
            {sortedOnboardingActions.map((action, index) => (
              <ActionRow
                key={action.id}
                action={action}
                isSelected={selectedIds.has(action.id)}
                onToggleSelection={onToggleSelection}
                orgId={orgId}
                onStartAction={onStartAction}
                onDismissAction={onDismissAction}
                firstCheckboxRef={
                  index === 0 ? firstActionCheckboxRef : undefined
                }
                isOnboarding
              />
            ))}
          </div>
        </div>
      );
    }

    // Still analyzing - show skeleton rows. If the first actions are gated on the
    // user creating their deal pipeline, say so instead of "analyzing".
    if (hasStartedOnboarding) {
      return (
        <div className='space-y-3'>
          {renderHeader()}
          <p className='text-[13px] text-neutral-500 dark:text-neutral-400'>
            {isWaitingForPipeline
              ? "We'll surface your first actions once your deal pipeline is set up."
              : 'Analyzing your conversations…'}
          </p>
          {/* Single skeleton row for the first action */}
          <div className='space-y-1.5'>
            <div className='h-[72px] bg-neutral-100 dark:bg-neutral-800 rounded-lg animate-pulse' />
          </div>
        </div>
      );
    }

    // No onboarding actions - fall through to show regular actions below
  }

  // Loading state: rows are still in flight — hold the section's height with
  // skeleton rows (the same ones the page skeleton uses) rather than flashing
  // the empty state and then pushing content in underneath it.
  if (isLoading && actions.length === 0) {
    return (
      <div className='space-y-3' aria-busy='true'>
        {renderHeader()}
        <div className='space-y-1.5'>
          {LOADING_ROW_KEYS.map(key => (
            <ActionRowSkeleton key={key} />
          ))}
        </div>
      </div>
    );
  }

  // Empty state: no actions available
  if (actions.length === 0) {
    return (
      <div className='space-y-3'>
        {renderHeader()}
        <div className='text-center py-8 text-[13px] text-neutral-500 dark:text-neutral-400'>
          You're all caught up. We'll surface new actions as they come in.
        </div>
      </div>
    );
  }

  // Normal state: show action rows
  return (
    <div className='space-y-3'>
      {renderHeader()}
      <div className='space-y-1.5'>
        {displayedActions.map((action, index) => (
          <ActionRow
            key={action.id}
            action={action}
            isSelected={selectedIds.has(action.id)}
            onToggleSelection={onToggleSelection}
            orgId={orgId}
            onStartAction={onStartAction}
            onDismissAction={onDismissAction}
            firstCheckboxRef={index === 0 ? firstActionCheckboxRef : undefined}
          />
        ))}
      </div>
    </div>
  );
}
